/**
 * BETA — Aide à la Décision (procurement assistance).
 */
import { httpError } from "../router.js";
import { paginate, newId, nowIso, todayYmd, money } from "./_utils.js";

// Cache the offre synthetic catalogue once per store role so the list is stable.
let cachedOffres = null;
let cachedFor = null;

export function register(route) {
  // BesoinPage.jsx reads the response as `r.data` and does
  // `Array.isArray(res) ? res : []` THEN `res.total`. So we must return an
  // ARRAY at the envelope's `data` slot, with a `total` property attached to
  // that array. The router's toEnvelope wraps our return as { data: <ret> },
  // so returning { data: rowsArray, total } makes r.data === rowsArray and
  // r.total === total. We attach total onto the array too for belt-and-braces.
  // Row fields the page renders: id, nom_commercial, dci/forme/dosage,
  // criticite, valeur, stock_actuel, stock_maximal, besoin_qty, seuil_critique,
  // fournisseur_lie:{nom}.
  route("GET", "/aide-decision/besoin", (ctx) => {
    const { params, store } = ctx;
    const CRITS = ["IMPORTANT", "MOINS_IMPORTANT", "NON_IMPORTANT"];
    const VALS = ["SUPERIEUR", "COMMUN"];
    let rows = store.data.produits
      .filter((p) => p.statut_produit !== "BLOQUE_CACHE")
      .filter((p) => (p.stock_actuel || 0) < (p.seuil_alerte || 0))
      .map((p) => {
        const seuil = p.seuil_alerte || 10;
        const stockMax = seuil * 4;
        const besoin = Math.max(1, stockMax - (p.stock_actuel || 0));
        const frnIdx = (p.id.charCodeAt(p.id.length - 1) || 0) % Math.max(1, store.data.fournisseurs.length);
        const frn = store.data.fournisseurs[frnIdx];
        // deterministic criticite/valeur from id so filters have variety
        const crit = CRITS[(p.id.charCodeAt(p.id.length - 1) || 0) % 3];
        const val = VALS[(p.id.charCodeAt(p.id.length - 2) || 0) % 2];
        return {
          id: p.id,
          produit_id: p.id,
          nom_commercial: p.nom_commercial,
          dci: p.dci || "",
          forme: p.forme_pharma || "",
          dosage: p.dosage || "",
          criticite: p.criticite_besoin || crit,
          valeur: p.valeur_besoin || val,
          stock_actuel: p.stock_actuel || 0,
          stock_maximal: stockMax,
          seuil_critique: Math.round(seuil / 2),
          besoin_qty: besoin,
          // both shapes: object for the page, *_nom/_id for other readers
          fournisseur_lie: frn ? { id: frn.id, nom: frn.nom } : null,
          fournisseur_id: frn?.id ?? null,
          fournisseur_nom: frn?.nom ?? null,
          fournisseur: frn ? { id: frn.id, nom: frn.nom } : null,
          qte_suggeree: besoin,
          qte_recommandee: besoin,
          prix_estime_unitaire: money((p.ppa || 0) * 0.6),
          montant_estime: money((p.ppa || 0) * 0.6 * besoin),
          // fournisseurs_disponibles powers the multi-select precommande modal
          fournisseurs_disponibles: store.data.fournisseurs
            .slice(0, 3)
            .map((f) => ({ id: f.id, nom: f.nom })),
        };
      });

    // filters
    if (params.criticite) rows = rows.filter((r) => r.criticite === params.criticite);
    if (params.valeur) rows = rows.filter((r) => r.valeur === params.valeur);
    if (params.search) {
      const q = String(params.search).toLowerCase();
      rows = rows.filter(
        (r) =>
          r.nom_commercial.toLowerCase().includes(q) ||
          (r.dci || "").toLowerCase().includes(q),
      );
    }

    const total = rows.length;
    const page = Number(params.page) || 1;
    const limit = Number(params.limit) || 20;
    const start = (page - 1) * limit;
    const slice = rows.slice(start, start + limit);
    // Attach total onto the array so `r.data.total` works after unwrap.
    slice.total = total;
    slice.page = page;
    slice.limit = limit;
    return { data: slice, total, page, limit };
  });

  route("POST", "/aide-decision/besoin/precommande", (ctx) => {
    // The BesoinPage sends { fournisseur_ids:[...], lignes:[{produit_id,
    // fournisseur_id, quantite}] }. Older callers send { items:[...] }. Accept
    // both. Lines without a fournisseur_id fall back to the first picked id.
    const body = ctx.body || {};
    const lines = body.lignes || body.items || [];
    const pickedIds = body.fournisseur_ids || [];
    const groups = new Map();
    for (const it of lines) {
      const fid = it.fournisseur_id || pickedIds[0] || "frn-001";
      if (!groups.has(fid)) groups.set(fid, []);
      groups.get(fid).push({ ...it, fournisseur_id: fid });
    }
    let count = 0;
    for (const [frnId, lines] of groups) {
      const frn = ctx.store.data.fournisseurs.find((f) => f.id === frnId) || ctx.store.data.fournisseurs[0];
      const id = newId("pc");
      ctx.store.data.efactures.unshift({
        id,
        numero: `PC-2026-${id.slice(-5).toUpperCase()}`,
        fournisseur_id: frn.id,
        fournisseur: { id: frn.id, nom: frn.nom, telephone: frn.telephone, email: frn.email },
        type: "PRECOMMANDE",
        statut: "PRECOMMANDE_ENVOYEE",
        statut_paiement: "NON_PAYE",
        date_emission: todayYmd(),
        bons_livraison: [],
        details: lines.map((ln, i) => ({
          id: `${id}-det-${i + 1}`,
          produit_id: ln.produit_id,
          quantite: ln.quantite,
          prix_achat_ht: 0,
          montant_ligne_ht: 0,
          montant_ligne_ttc: 0,
        })),
        montant_ht: 0,
        montant_ttc: 0,
        montant_tva: 0,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
      count += 1;
    }
    ctx.commit();
    return { count, message: `${count} pré-commande(s) créée(s) (démo)` };
  });

  route("GET", "/aide-decision/offres", (ctx) => {
    const offres = getOffres(ctx.store);
    return paginate(offres, ctx.params);
  });

  route("GET", "/aide-decision/offres/:id", (ctx) => {
    const offres = getOffres(ctx.store);
    const o = offres.find((x) => x.id === ctx.params.id);
    if (!o) httpError(404, "Offre introuvable", "NOT_FOUND");
    return o;
  });

  route("POST", "/aide-decision/offres/:id/accepter", (ctx) => {
    const offres = getOffres(ctx.store);
    const o = offres.find((x) => x.id === ctx.params.id);
    if (!o) httpError(404, "Offre introuvable", "NOT_FOUND");
    o.statut = "ACCEPTEE";
    return o;
  });

  route("POST", "/aide-decision/offres/:id/rejeter", (ctx) => {
    const offres = getOffres(ctx.store);
    const o = offres.find((x) => x.id === ctx.params.id);
    if (!o) httpError(404, "Offre introuvable", "NOT_FOUND");
    o.statut = "REJETEE";
    o.motif_rejet = ctx.body?.motif || "";
    return o;
  });

  route("GET", "/aide-decision/offres-manuelles", (ctx) =>
    paginate(ctx.store.data.offresManuelles || [], ctx.params),
  );

  // X XWP-32 — OffreManuelle is frozen: no creation route (the server answers 404 too).

  route("PATCH", "/aide-decision/offres-manuelles/:id/valider", (ctx) =>
    mutateOffreManuelle(ctx, { statut: "VALIDEE" }),
  );
  route("PATCH", "/aide-decision/offres-manuelles/:id/recu", (ctx) =>
    mutateOffreManuelle(ctx, { statut: "RECU", date_reception: todayYmd() }),
  );
  route("PATCH", "/aide-decision/offres-manuelles/:id/archiver", (ctx) =>
    mutateOffreManuelle(ctx, { archive: true }),
  );

  route("GET", "/aide-decision/settings", (ctx) => ctx.store.data.settings.aide_decision || {});
  route("PUT", "/aide-decision/settings", (ctx) => {
    ctx.store.data.settings.aide_decision = { ...(ctx.store.data.settings.aide_decision || {}), ...(ctx.body || {}) };
    ctx.commit();
    return ctx.store.data.settings.aide_decision;
  });

  // ── SOTA DSS — Prévisions / Explain / Config / ABC ────────────────────────
  // Mirror server/src/services/aideDecision.service.js shape so the UI behaves
  // identically against the real backend.
  // The DSS pages read r.data as the array (or r.data.data) — return the
  // array DIRECTLY at the envelope's data slot (the router wraps return in
  // { data: <ret> }, so a plain array makes r.data === array).
  route("GET", "/aide-decision/previsions", (ctx) => {
    const cfg = ctx.store.data.settings.decision_config || DEFAULT_DECISION_CONFIG;
    return buildDssPrevisions(ctx.store, cfg);
  });

  route("GET", "/aide-decision/abc-classification", (ctx) => buildAbc(ctx.store));

  route("GET", "/aide-decision/explain/:produit_id", (ctx) => {
    const cfg = ctx.store.data.settings.decision_config || DEFAULT_DECISION_CONFIG;
    const explain = explainOne(ctx.store, ctx.params.produit_id, cfg);
    if (!explain) httpError(404, "Produit introuvable", "NOT_FOUND");
    return explain;
  });

  route("GET", "/aide-decision/config", (ctx) => {
    const cfg = ctx.store.data.settings.decision_config || {};
    return { ...DEFAULT_DECISION_CONFIG, ...cfg };
  });

  route("PUT", "/aide-decision/config", (ctx) => {
    ctx.store.data.settings.decision_config = {
      ...DEFAULT_DECISION_CONFIG,
      ...(ctx.store.data.settings.decision_config || {}),
      ...(ctx.body || {}),
    };
    ctx.commit();
    return ctx.store.data.settings.decision_config;
  });

  route("POST", "/aide-decision/config/reset", (ctx) => {
    ctx.store.data.settings.decision_config = { ...DEFAULT_DECISION_CONFIG };
    ctx.commit();
    return ctx.store.data.settings.decision_config;
  });

  route("GET", "/aide-decision/overrides", (ctx) =>
    ctx.store.data.decision_overrides || [],
  );
  route("POST", "/aide-decision/overrides", (ctx) => {
    if (!ctx.store.data.decision_overrides) ctx.store.data.decision_overrides = [];
    const created = {
      id: newId("ovr"),
      created_at: nowIso(),
      ...(ctx.body || {}),
    };
    ctx.store.data.decision_overrides.unshift(created);
    ctx.commit();
    return created;
  });
  route("DELETE", "/aide-decision/overrides/:id", (ctx) => {
    ctx.store.data.decision_overrides = (ctx.store.data.decision_overrides || []).filter(
      (o) => o.id !== ctx.params.id,
    );
    ctx.commit();
    return { ok: true };
  });

  route("GET", "/aide-decision/seasonal-events", (ctx) =>
    ctx.store.data.seasonal_events || DEFAULT_SEASONAL_EVENTS,
  );
  route("POST", "/aide-decision/seasonal-events", (ctx) => {
    if (!ctx.store.data.seasonal_events) ctx.store.data.seasonal_events = [...DEFAULT_SEASONAL_EVENTS];
    const created = {
      id: newId("se"),
      actif: true,
      created_at: nowIso(),
      ...(ctx.body || {}),
    };
    ctx.store.data.seasonal_events.unshift(created);
    ctx.commit();
    return created;
  });
  route("PATCH", "/aide-decision/seasonal-events/:id", (ctx) => {
    if (!ctx.store.data.seasonal_events) ctx.store.data.seasonal_events = [...DEFAULT_SEASONAL_EVENTS];
    const idx = ctx.store.data.seasonal_events.findIndex((e) => e.id === ctx.params.id);
    if (idx < 0) httpError(404, "Événement introuvable", "NOT_FOUND");
    ctx.store.data.seasonal_events[idx] = { ...ctx.store.data.seasonal_events[idx], ...(ctx.body || {}) };
    ctx.commit();
    return ctx.store.data.seasonal_events[idx];
  });
  route("DELETE", "/aide-decision/seasonal-events/:id", (ctx) => {
    ctx.store.data.seasonal_events = (ctx.store.data.seasonal_events || DEFAULT_SEASONAL_EVENTS).filter(
      (e) => e.id !== ctx.params.id,
    );
    ctx.commit();
    return { ok: true };
  });

  // PATCH override (used by Surcharges tab)
  route("PATCH", "/aide-decision/overrides/:id", (ctx) => {
    if (!ctx.store.data.decision_overrides) ctx.store.data.decision_overrides = [];
    const idx = ctx.store.data.decision_overrides.findIndex((o) => o.id === ctx.params.id);
    if (idx < 0) httpError(404, "Surcharge introuvable", "NOT_FOUND");
    ctx.store.data.decision_overrides[idx] = { ...ctx.store.data.decision_overrides[idx], ...(ctx.body || {}) };
    ctx.commit();
    return ctx.store.data.decision_overrides[idx];
  });
}

// ── DSS demo helpers ────────────────────────────────────────────────────────

const DEFAULT_DECISION_CONFIG = {
  mu_important: 1.5,
  mu_moins_important: 1.0,
  mu_non_important: 0.7,
  safety_factor: 1.2,
  lead_time_default: 7,
  horizon_days: 30,
  profit_mu_valuable: 1.3,
  profit_mu_secondaire: 1.0,
  ewma_alpha: 0.3,
  service_level_important: 0.95,
  service_level_moins: 0.9,
  service_level_non: 0.8,
  cv_gate_hw: 0.5,
  cv_gate_croston: 1.0,
  technique_preference: "AUTO",
};

const DEFAULT_SEASONAL_EVENTS = [
  { id: "se-ramadan-2026", nom: "Ramadan 2026", date_debut: "2026-02-11", date_fin: "2026-03-12",
    multipliers: { ANALGESIQUE: 1.6, ANTACIDE: 1.8, ORS: 1.4, VITAMINES: 1.3 }, actif: true },
  { id: "se-hiver-2026", nom: "Hiver — saison grippale", date_debut: "2026-12-01", date_fin: "2026-02-28",
    multipliers: { ANTITUSSIF: 1.5, ANTIPYRETIQUE: 1.4 }, actif: true },
  { id: "se-ete-2026", nom: "Été — chaleur", date_debut: "2026-06-01", date_fin: "2026-08-31",
    multipliers: { ORS: 1.6, SOLAIRE: 2.0, ANTIDIARRHEIQUE: 1.3 }, actif: false },
];

const TECHNIQUES = ["EWMA", "HOLT_WINTERS", "CROSTON", "SMA"];
const URGENCIES = ["NORMAL", "URGENT", "CRITIQUE", "RUPTURE"];

function pickTechniqueDemo(cv) {
  if (cv > 1.0) return "CROSTON";
  if (cv > 0.5) return "HOLT_WINTERS";
  return "EWMA";
}

// Deterministic μ for a produit's importance — falls back to MOINS if unset.
function muFor(produit, cfg) {
  const imp = produit.criticite || produit.importance || "MOINS_IMPORTANT";
  if (imp === "IMPORTANT") return Number(cfg.mu_important);
  if (imp === "NON_IMPORTANT") return Number(cfg.mu_non_important);
  return Number(cfg.mu_moins_important);
}

function profitFor(produit, cfg) {
  const cls = produit.valeur_besoin === "SUPERIEUR" ? "VALUABLE" : produit.profit_class || "SECONDAIRE";
  return cls === "VALUABLE" ? Number(cfg.profit_mu_valuable) : Number(cfg.profit_mu_secondaire);
}

function zFor(produit, cfg) {
  const imp = produit.criticite || produit.importance || "MOINS_IMPORTANT";
  const sl = imp === "IMPORTANT" ? cfg.service_level_important
    : imp === "NON_IMPORTANT" ? cfg.service_level_non
    : cfg.service_level_moins;
  // Beasley-Springer-Moro approximation, rounded.
  if (sl >= 0.95) return 1.65;
  if (sl >= 0.9) return 1.28;
  return 0.84;
}

/** Build the under-min DSS row list from store.produits + a deterministic seed. */
function buildDssPrevisions(store, cfg) {
  return store.data.produits
    .filter((p) => p.statut_produit !== "BLOQUE_CACHE")
    .map((p) => buildDssRow(p, cfg, store))
    .filter((r) => r.stock_actuel <= r.min)
    .sort((a, b) => {
      const ord = { RUPTURE: 0, CRITIQUE: 1, URGENT: 2, NORMAL: 3 };
      const u = (ord[a.urgency] ?? 4) - (ord[b.urgency] ?? 4);
      if (u !== 0) return u;
      return a.days_of_cover - b.days_of_cover;
    });
}

function buildDssRow(p, cfg, store) {
  const stockActuel = Number(p.stock_actuel ?? 0);
  // Deterministic daily-average proxy (vélocité-like) from the product id —
  // tuned so ~15-25% of demo products surface as under-min (realistic for a
  // pharmacy with healthy chronique-CNAS demand).
  const seed = (p.id.charCodeAt(p.id.length - 1) || 7) % 13;
  const avgDaily = Math.max(0.4, (seed + 2) * 0.8);
  const sigma = avgDaily * (0.25 + seed * 0.04);
  const cv = sigma / Math.max(avgDaily, 1e-6);
  const technique = pickTechniqueDemo(cv);
  const muEff = muFor(p, cfg);
  const profitMu = profitFor(p, cfg);
  const z = zFor(p, cfg);
  const lead = p.lead_time_jours || cfg.lead_time_default;
  const ss = Math.ceil(z * sigma * Math.sqrt(lead));
  const seasonal = 1.0;
  const eventMult = 1.0;
  const adjusted = avgDaily * seasonal * eventMult;
  const min = Math.ceil(adjusted * lead * muEff * cfg.safety_factor) + Math.ceil(adjusted * 3) + ss;
  const max = min + Math.ceil(adjusted * cfg.horizon_days * muEff * profitMu);
  const suggested = Math.max(0, max - stockActuel);
  const dayCover = Math.min(999, stockActuel / Math.max(adjusted, 0.01));
  const urgency =
    stockActuel === 0 ? "RUPTURE"
    : stockActuel < min && dayCover < lead ? "CRITIQUE"
    : dayCover < lead * 2 ? "URGENT"
    : "NORMAL";
  const lab = store.data.laboratoires?.find((l) => l.id === p.laboratoire_id);
  return {
    produit_id: p.id,
    nom_commercial: p.nom_commercial,
    laboratoire: lab ? { id: lab.id, nom: lab.nom } : (p.laboratoire_nom ? { id: null, nom: p.laboratoire_nom } : null),
    importance: p.criticite || p.importance || "MOINS_IMPORTANT",
    profit_class: p.valeur_besoin === "SUPERIEUR" ? "VALUABLE" : (p.profit_class || "SECONDAIRE"),
    stock_actuel: stockActuel,
    avg_daily: round2(adjusted),
    min,
    max,
    suggested_order_qty: suggested,
    days_of_cover: round2(dayCover),
    urgency,
    technique,
    explain: {
      logic: `${technique}(α=${cfg.ewma_alpha}) × μ_eff(${muEff} ${p.criticite ?? "MOINS"}) × profit(×${profitMu}) + SS(z·σ·√L=${ss})`,
      variables: {
        mu_hist: round2(avgDaily), sigma: round2(sigma), cv: round2(cv), adi: 1.0,
        technique, mu_effective: muEff, profit_factor: profitMu, z,
        seasonal_factor: seasonal, event_mult: eventMult,
        lead_time: lead, ss, avg_daily: round2(adjusted),
      },
      override_applied: { min: false, max: false, mu: false },
      citations: technique === "EWMA"
        ? ["Brown 1959 (EWMA)", "Silver/Pyke/Peterson 1998 Ch.7.5"]
        : technique === "CROSTON"
          ? ["Croston 1972 ORQ 23(3)", "Syntetos-Boylan 2005 JORS 56(5)"]
          : ["Winters 1960 Mgmt Sci 6(3)", "Hyndman & Athanasopoulos 2021 Ch.8.3"],
    },
  };
}

function explainOne(store, produitId, cfg) {
  const p = store.data.produits.find((x) => x.id === produitId);
  if (!p) return null;
  return buildDssRow(p, cfg, store);
}

function buildAbc(store) {
  // 90-day revenue Pareto from cached produits (deterministic).
  const items = store.data.produits
    .filter((p) => p.ppa)
    .map((p) => {
      const seed = (p.id.charCodeAt(p.id.length - 1) || 7) % 13;
      const dailyQty = (seed + 1) * 0.18;
      const revenue = money(dailyQty * 90 * Number(p.ppa ?? 0));
      return { produit_id: p.id, nom: p.nom_commercial, revenue };
    })
    .sort((a, b) => b.revenue - a.revenue);
  const total = items.reduce((s, r) => s + r.revenue, 0) || 1;
  let cumul = 0;
  return items.map((r) => {
    cumul += r.revenue;
    const share = cumul / total;
    return { ...r, cumulative_share: round2(share), class: share <= 0.8 ? "A" : share <= 0.95 ? "B" : "C" };
  });
}

function round2(n) { return Math.round(Number(n) * 100) / 100; }

function mutateOffreManuelle(ctx, patch) {
  const o = (ctx.store.data.offresManuelles || []).find((x) => x.id === ctx.params.id);
  if (!o) {
    const e = new Error("Offre manuelle introuvable");
    e.status = 404;
    throw e;
  }
  Object.assign(o, patch);
  ctx.commit();
  return o;
}

function getOffres(store) {
  if (cachedOffres && cachedFor === store.role) return cachedOffres;
  cachedOffres = [];
  const statuts = ["PROPOSEE", "PROPOSEE", "ACCEPTEE", "PROPOSEE", "EXPIREE"];
  const produitsSlice = store.data.produits.filter((p) => p.is_remboursable).slice(0, 8);
  produitsSlice.forEach((p, i) => {
    const frn = store.data.fournisseurs[i % store.data.fournisseurs.length];
    const qty = 50 + (i * 12) % 100;
    const pu = money((p.ppa || 0) * (0.55 - (i % 5) * 0.02));
    cachedOffres.push({
      id: `offre-${i + 1}`,
      fournisseur_id: frn.id,
      fournisseur_nom: frn.nom,
      produit_id: p.id,
      produit_nom: p.nom_commercial,
      quantite: qty,
      prix_unitaire: pu,
      montant_total: money(pu * qty),
      date_offre: new Date(Date.now() - i * 86400000).toISOString().slice(0, 10),
      date_expiration: new Date(Date.now() + (15 - i) * 86400000).toISOString().slice(0, 10),
      statut: statuts[i % statuts.length],
    });
  });
  cachedFor = store.role;
  return cachedOffres;
}
